import React, { useState, useEffect } from 'react';
import { DndContext, useDroppable } from '@dnd-kit/core';
import TaskFilters from '../../../../features/filters/components/TaskFilters/TaskFilters';
import TaskList from './TaskList/TaskList';
import FileName from '../../../../features/files/components/FileName/FileName';
import SaveButton from '../../../../ui/components/SaveButton/SaveButton';
import PrioritizeToolbar from './PrioritizeToolbar/PrioritizeToolbar';
import TaskTableListHeader from './TaskTableListHeader';
import ViewModeSwitch from './ViewModeSwitch';
import { getPrioritizeSectionName, getPrioritizeSectionOrder } from '../../utils/taskUtils';
import './TaskTable.css';

// Component to make a section header droppable (optional onClick for mobile tab)
function DroppableSectionHeader({ section, dragOverSection, dragOverSectionHeader, dropPosition, sectionTasks, onClick, isActive, isMovedTo, ariaLabel, isDragActive, children }) {
  const { setNodeRef } = useDroppable({
    id: `section-${section}`,
  });

  // Only highlight when pointer is over this header (not when dragging over a task in the section)
  const isDragOver = dragOverSectionHeader === section;
  const hasTasks = sectionTasks && sectionTasks.length > 0;
  // Show blue line below header only when pointer is over the header itself
  const showDropIndicator = isDragOver && hasTasks && dropPosition === 'after';
  const dropClass = showDropIndicator ? 'prioritize-section-header-drop-after' : '';
  const activeClass = isActive ? 'prioritize-section-header-active' : '';
  const movedToClass = isMovedTo ? `prioritize-section-header-moved-to prioritize-section-header-moved-to-${section}` : '';
  /* On mobile (when onClick is set for tab switching), always show drop zone so users can see where to drop */
  const showDropZone = Boolean(onClick);
  const taskCount = sectionTasks ? sectionTasks.length : 0;
  const dropZoneLabel = `${taskCount} task${taskCount === 1 ? '' : 's'}`;

  return (
    <div
      ref={setNodeRef}
      id={`section-${section}`}
      data-section={section}
      role={onClick ? 'tab' : undefined}
      aria-selected={onClick && isActive ? true : undefined}
      aria-label={ariaLabel}
      className={`prioritize-section-header ${isDragOver ? 'drag-over' : ''} ${dropClass} ${activeClass} ${movedToClass} ${showDropZone ? 'prioritize-section-header-with-drop-zone' : ''}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {children}
      {showDropZone && (
        <div className="prioritize-section-header-drop-zone" aria-hidden="true">
          {dropZoneLabel}
        </div>
      )}
    </div>
  );
}

/**
 * Prioritize view component for TaskTable
 * Displays tasks grouped by prioritize sections (no-due-date, today, later)
 * Overdue tasks are included in the no-due-date section
 */
export default function TaskTablePrioritizeView({
  tasks,
  prioritizeGroups,
  visiblePrioritizeSections,
  togglePrioritizeSectionVisibility,
  currentFileName,
  editingFileName,
  onFileNameClick,
  onFileNameBlur,
  onFileNameKeyDown,
  currentFileId,
  files,
  currentFile,
  hasUnsavedChanges,
  hasRestoredDraft = false,
  loggedOutWorkspaceSnapshot = null,
  sensors,
  collisionDetection,
  onDragOver,
  onDragStart,
  onDragEnd,
  onDragCancel,
  expandedTasks,
  editingTaskId,
  editingDueDateTaskId,
  blockingTasksHighlight,
  filter,
  timeFilter,
  editingElementRef,
  editHighlightAnchorRef,
  onEditHighlightAnchorReady,
  dateInputCallbackRef,
  onToggleExpand,
  onCheckboxChange,
  onSetEditingTaskId,
  onSetEditingDueDateTaskId,
  onUpdateTaskDueDate,
  onTaskTextClick,
  onTaskTextFocus,
  onTaskTextInput,
  onTaskContentClick,
  onTaskTextBlur,
  onTaskTextKeyDown,
  onTaskTextPaste,
  onBlockedIconMouseEnter,
  onBlockedIconMouseLeave,
  dragOverTaskId,
  dropPosition,
  dragOverSection,
  dragOverSectionHeader,
  isDragActive,
  renderTask,
  onFilterChange,
  topToolbarFlag = false,
  onAddTaskInPrioritizeSection,
  isLoggedIn = false
}) {
  // Section keys in display order; filter by visibility
  const allSectionKeys = ['no-due-date', 'today', 'work-in-progress', 'later', 'done'];
  const visibleSectionKeys = allSectionKeys
    .filter(section => visiblePrioritizeSections.has(section))
    .sort((a, b) => getPrioritizeSectionOrder(a) - getPrioritizeSectionOrder(b));

  const DESKTOP_BREAKPOINT_PX = 769;
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`).matches
  );
  const [activeMobileSection, setActiveMobileSection] = useState(() => visibleSectionKeys[0] ?? null);

  useEffect(() => {
    const m = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`);
    const handler = () => setIsDesktop(m.matches);
    m.addEventListener('change', handler);
    return () => m.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (visibleSectionKeys.length === 0) {
      setActiveMobileSection(null);
      return;
    }
    setActiveMobileSection(prev => (prev && visibleSectionKeys.includes(prev) ? prev : visibleSectionKeys[0]));
  }, [visibleSectionKeys]);

  /* Top-toolbar variant: same header as Organize (filename + toolbar row with mode switch) */
  const topHeaderLeftContent = topToolbarFlag ? (
    <TaskTableListHeader
      fileName={currentFileName}
      isEditing={editingFileName}
      onFileNameClick={onFileNameClick}
      onFileNameBlur={onFileNameBlur}
      onFileNameKeyDown={onFileNameKeyDown}
      tasks={tasks}
      currentFileId={currentFileId}
      files={files}
      currentFile={currentFile}
      hasUnsavedChanges={hasUnsavedChanges}
      hasRestoredDraft={hasRestoredDraft}
      loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
      editHighlightAnchorRef={editHighlightAnchorRef}
      onEditHighlightAnchorReady={onEditHighlightAnchorReady}
    />
  ) : null;

  const topHeaderToolbarRow = topToolbarFlag && onFilterChange ? (
    <div className="task-view-top-toolbar-inner">
      {!isLoggedIn && (
        <SaveButton
          tasks={tasks}
          currentFileId={currentFileId}
          currentFileName={currentFileName || ''}
          hasUnsavedChanges={hasUnsavedChanges}
          hasRestoredDraft={hasRestoredDraft}
          loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
          alwaysVisible
        />
      )}
      <PrioritizeToolbar
        visiblePrioritizeSections={visiblePrioritizeSections}
        onTogglePrioritizeSectionVisibility={togglePrioritizeSectionVisibility}
      />
      <ViewModeSwitch filter={filter} onFilterChange={onFilterChange} />
    </div>
  ) : null;

  return (
    <>
      <TaskFilters
        tasks={tasks}
        visiblePrioritizeSections={visiblePrioritizeSections}
        onTogglePrioritizeSectionVisibility={togglePrioritizeSectionVisibility}
        leftContent={topHeaderLeftContent}
        toolbarRow={topHeaderToolbarRow}
        modeSwitchInToolbar={topToolbarFlag}
      />
      <div className="task-table-content">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragOver={onDragOver}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div className="task-list-container">
          {currentFileName && !topToolbarFlag && (
            <div className="task-view-filename-row">
              <FileName
                fileName={currentFileName}
                isEditing={editingFileName}
                onFileNameClick={onFileNameClick}
                onFileNameBlur={onFileNameBlur}
                onFileNameKeyDown={onFileNameKeyDown}
                tasks={tasks}
                currentFileId={currentFileId}
                files={files}
                currentFile={currentFile}
                hasUnsavedChanges={hasUnsavedChanges}
                hasRestoredDraft={hasRestoredDraft}
                loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
                editHighlightAnchorRef={editHighlightAnchorRef}
                onEditHighlightAnchorReady={onEditHighlightAnchorReady}
              />
              <div className="task-view-logo" aria-hidden="true">
                <svg width="130" height="40" viewBox="0 0 130 40" xmlns="http://www.w3.org/2000/svg">
                  <text x="10" y="28" fontFamily="system-ui, -apple-system, sans-serif" fontSize="24" fontWeight="600">
                    <tspan fill="#444" fontSize="24">pico</tspan><tspan fill="#333" fontSize="28" dx="1">todo</tspan>
                  </text>
                </svg>
                <span className="task-view-logo-badge">BETA</span>
              </div>
            </div>
          )}
          {isDesktop ? (
          <div className="prioritize-sections">
          {visibleSectionKeys.map(section => {
            const sectionTasks = prioritizeGroups[section] || [];
            return (
              <div
                key={section}
                className={`prioritize-section ${dragOverSection === section ? 'drag-over' : ''} ${section === 'today' ? 'prioritize-section-today' : ''} ${section === 'work-in-progress' ? 'prioritize-section-work-in-progress' : ''} ${section === 'later' ? 'prioritize-section-later' : ''} ${section === 'done' ? 'prioritize-section-done' : ''}`}
              >
                <DroppableSectionHeader
                  section={section}
                  dragOverSection={dragOverSection}
                  dragOverSectionHeader={dragOverSectionHeader}
                  dropPosition={dragOverSection === section ? dropPosition : null}
                  sectionTasks={sectionTasks}
                >
                  {getPrioritizeSectionName(section)}
                </DroppableSectionHeader>
                <TaskList
                  tasks={sectionTasks}
                  expandedTasks={expandedTasks}
                  editingTaskId={editingTaskId}
                  editingDueDateTaskId={editingDueDateTaskId}
                  blockingTasksHighlight={blockingTasksHighlight}
                  filter={filter}
                  timeFilter={timeFilter}
                  editingElementRef={editingElementRef}
                  dateInputCallbackRef={dateInputCallbackRef}
                  onToggleExpand={onToggleExpand}
                  onCheckboxChange={onCheckboxChange}
                  onSetEditingTaskId={onSetEditingTaskId}
                  onSetEditingDueDateTaskId={onSetEditingDueDateTaskId}
                  onUpdateTaskDueDate={onUpdateTaskDueDate}
                  onTaskTextClick={onTaskTextClick}
                  onTaskTextFocus={onTaskTextFocus}
                  onTaskTextInput={onTaskTextInput}
                  onTaskContentClick={onTaskContentClick}
                  onTaskTextBlur={onTaskTextBlur}
                  onTaskTextKeyDown={onTaskTextKeyDown}
                  onTaskTextPaste={onTaskTextPaste}
                  onBlockedIconMouseEnter={onBlockedIconMouseEnter}
                  onBlockedIconMouseLeave={onBlockedIconMouseLeave}
                  renderTask={renderTask}
                  onAddTaskAfterRoot={() => onAddTaskInPrioritizeSection?.(section)}
                  showPlaceholderWhenEmpty={false}
                  disableSequentialAdd={true}
                />
              </div>
            );
          })}
          </div>
          ) : (
          <>
            <div className="prioritize-mobile-tabs" role="tablist">
              {visibleSectionKeys.map(section => {
                const sectionTasks = prioritizeGroups[section] || [];
                const tabLabel = section === 'work-in-progress'
                  ? 'WIP'
                  : section === 'no-due-date' && visibleSectionKeys.includes('work-in-progress')
                    ? 'Unprio...'
                    : section === 'done'
                      ? 'Done'
                      : getPrioritizeSectionName(section);
                const tabContent = tabLabel;
                const tabAriaLabel = tabLabel !== getPrioritizeSectionName(section) ? getPrioritizeSectionName(section) : undefined;
                return (
                  <DroppableSectionHeader
                    key={section}
                    section={section}
                    ariaLabel={tabAriaLabel}
                    dragOverSection={dragOverSection}
                    dragOverSectionHeader={dragOverSectionHeader}
                    dropPosition={dragOverSection === section ? dropPosition : null}
                    sectionTasks={sectionTasks}
                    onClick={() => setActiveMobileSection(section)}
                    isActive={activeMobileSection === section}
                    isMovedTo={false}
                    isDragActive={isDragActive}
                  >
                    {tabContent}
                  </DroppableSectionHeader>
                );
              })}
            </div>
            <div className="prioritize-mobile-panel">
              {activeMobileSection && (() => {
                const sectionTasks = prioritizeGroups[activeMobileSection] || [];
                return (
                  <>
                    <TaskList
                      tasks={sectionTasks}
                      expandedTasks={expandedTasks}
                      editingTaskId={editingTaskId}
                      editingDueDateTaskId={editingDueDateTaskId}
                      blockingTasksHighlight={blockingTasksHighlight}
                      filter={filter}
                      timeFilter={timeFilter}
                      editingElementRef={editingElementRef}
                      dateInputCallbackRef={dateInputCallbackRef}
                      onToggleExpand={onToggleExpand}
                      onCheckboxChange={onCheckboxChange}
                      onSetEditingTaskId={onSetEditingTaskId}
                      onSetEditingDueDateTaskId={onSetEditingDueDateTaskId}
                      onUpdateTaskDueDate={onUpdateTaskDueDate}
                      onTaskTextClick={onTaskTextClick}
                      onTaskTextFocus={onTaskTextFocus}
                      onTaskTextInput={onTaskTextInput}
                      onTaskContentClick={onTaskContentClick}
                      onTaskTextBlur={onTaskTextBlur}
                      onTaskTextKeyDown={onTaskTextKeyDown}
                      onTaskTextPaste={onTaskTextPaste}
                      onBlockedIconMouseEnter={onBlockedIconMouseEnter}
                      onBlockedIconMouseLeave={onBlockedIconMouseLeave}
                      renderTask={renderTask}
                      onAddTaskAfterRoot={() => onAddTaskInPrioritizeSection?.(activeMobileSection)}
                      showPlaceholderWhenEmpty={false}
                      disableSequentialAdd={true}
                    />
                  </>
                );
              })()}
            </div>
          </>
          )}
        </div>
      </DndContext>
      </div>
    </>
  );
}
